import React, { useEffect, useState, useRef } from 'react';
import { useCompanyStore, useAuthStore } from '@/store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Building2,
  Mail,
  Phone,
  MapPin,
  Clock,
  Save,
  Loader2,
  Upload,
  Image as ImageIcon,
  CreditCard,
  CalendarDays,
  AlertCircle,
  CheckCircle2,
  Timer,
  Shield,
  FileText,
  Lock,
  RefreshCw,
  AlertTriangle,
  Package,
  Building,
  Copy,
  CheckCircle,
  Users,
  MessageCircle,
  Info,
  History,
  ArrowRight,
  XCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { useI18n } from '@/components/I18nProvider';

const formatDateTR = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return '-';
  }
};

const getSubscriptionInfo = (company) => {
  if (!company) return { label: '-', color: 'secondary', icon: AlertCircle };
  const status = company.subscription_status;
  if (status === 'trial') {
    const trialEnd = company.trial_end ? new Date(company.trial_end) : null;
    const now = new Date();
    const daysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24))) : 0;
    return { label: `Deneme (${daysLeft} gün kaldı)`, color: 'bg-amber-100 text-amber-700 border-amber-300', icon: Timer };
  }
  if (status === 'active') return { label: 'Aktif Abonelik', color: 'bg-green-100 text-green-700 border-green-300', icon: CheckCircle2 };
  if (status === 'expired') return { label: 'Süresi Dolmuş', color: 'bg-red-100 text-red-700 border-red-300', icon: AlertCircle };
  if (status === 'pending_payment') return { label: 'Havale Onayı Bekliyor', color: 'bg-orange-100 text-orange-700 border-orange-300', icon: Timer };
  return { label: status, color: 'bg-gray-100 text-gray-700 border-gray-300', icon: AlertCircle };
};

const YEARLY_PRICES_FALLBACK = {};

const API_URL = process.env.REACT_APP_BACKEND_URL;

function RenewalSection({ company, packages, paymentMethods, showRenewal, setShowRenewal, renewPkg, setRenewPkg, renewPm, setRenewPm, isRenewing, setIsRenewing, renewSubscription, fetchCompany, user, setRenewPaytrToken, setRenewPaytrOid }) {
  if (!company) return null;

  const status = company.subscription_status;
  const subEnd = company.subscription_end ? new Date(company.subscription_end) : null;
  const trialEnd = company.trial_end ? new Date(company.trial_end) : null;
  const endDate = subEnd || trialEnd;
  const now = new Date();
  const daysLeft = endDate ? Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24))) : 999;

  // Show renewal when: ≤10 days left on active/trial, or expired, or pending_payment
  const needsRenewal = (status === 'active' && daysLeft <= 10) || status === 'expired' || status === 'trial';
  const isPending = status === 'pending_payment';

  if (!needsRenewal && !isPending) return null;

  const selectedPkg = packages.find(p => p.slug === renewPkg);
  const renewPkgData = packages.find(p => p.slug === renewPkg);
  const yearlyPrice = renewPkgData?.yearly_price || (renewPkgData?.monthly_price || 0) * 12;

  const copyIban = (iban) => {
    navigator.clipboard.writeText(iban).then(() => toast.success('IBAN kopyalandı'));
  };

  const handleRenew = async () => {
    if (!renewPkg) return toast.error('Paket seçin');
    if (!renewPm && paymentMethods.length > 0) return toast.error('Ödeme yöntemi seçin');
    
    const useCreditCard = renewPm?.type === 'credit_card' && renewPm?.has_payment_api;
    
    setIsRenewing(true);
    
    if (useCreditCard) {
      const renewPkgData = packages.find(p => p.slug === renewPkg);
      const renewAmount = renewPkgData?.yearly_price || (renewPkgData?.monthly_price || 0) * 12;
      try {
        const tRes = await axios.post(`${API_URL}/api/paytr/create-token`, {
          email: user?.email || company?.email || '',
          amount: renewAmount,
          user_name: user?.name || company?.contact_name || company?.name || 'Kullanıcı',
          user_phone: company?.phone || company?.contact_phone || user?.phone || '05000000000',
          user_address: company?.address || company?.name || 'Türkiye',
          package_name: renewPkgData?.name || 'Abonelik',
          package_slug: renewPkg,
          company_id: company?.id || '',
          flow: 'renewal',
          currency: 'TL',
          merchant_ok_url: `${window.location.origin}/settings`,
          merchant_fail_url: `${window.location.origin}/settings`,
        });
        if (tRes.data?.success) {
          setRenewPaytrToken(tRes.data.token);
          setRenewPaytrOid(tRes.data.merchant_oid);
          setIsRenewing(false);
          return;
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || 'PayTR ödeme başlatılamadı');
      }
      setIsRenewing(false);
    } else {
      const res = await renewSubscription(company.id, {
        package_slug: renewPkg,
        payment_method_type: renewPm?.type || null,
        payment_method_id: renewPm?.id || null,
      });
      setIsRenewing(false);
      if (res.success) {
        toast.success(res.data?.message || 'Abonelik yenileme talebiniz alındı');
        setShowRenewal(false);
        fetchCompany();
      } else {
        toast.error(res.error);
      }
    }
  };

  // If pending payment, show waiting message WITH bank details
  if (isPending) {
    const bankTransferMethods = paymentMethods.filter(pm => pm.type === 'bank_transfer' && pm.iban);
    return (
      <Card className="border-orange-200 bg-orange-50/50" data-testid="pending-payment-card">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-orange-100 p-2 text-orange-600 shrink-0">
              <Timer className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold text-orange-800">Havale Onayı Bekleniyor</h3>
              <p className="text-sm text-orange-700 mt-1">
                Ödemeniz henüz onaylanmadı. Havale/EFT yaptıktan sonra yönetici tarafından onaylandığında aboneliğiniz aktif edilecektir.
              </p>
              {company.registered_package_name && company.registered_package_name !== 'Ücretsiz Deneme' && (
                <p className="text-xs text-orange-600 mt-2">Seçilen paket: <span className="font-medium">{company.registered_package_name}</span></p>
              )}
            </div>
          </div>
          {/* Show IBAN details while pending */}
          {bankTransferMethods.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-orange-800 uppercase tracking-wider">Havale Bilgileri</p>
              {bankTransferMethods.map(pm => (
                <div key={pm.id} className="rounded-lg border border-orange-200 bg-white p-3 space-y-1 text-sm">
                  {pm.bank_name && <p className="text-orange-700">Banka: <span className="font-medium">{pm.bank_name}</span></p>}
                  <div className="flex items-center gap-2">
                    <p className="text-orange-700">IBAN: <span className="font-mono font-medium">{pm.iban}</span></p>
                    <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyIban(pm.iban)}><Copy className="h-3 w-3" /></Button>
                  </div>
                  {pm.account_holder && <p className="text-orange-700">Hesap Sahibi: <span className="font-medium">{pm.account_holder}</span></p>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Warning banner
  const isUrgent = daysLeft <= 10 && status === 'active';
  const isExpired = status === 'expired';

  return (
    <Card className={`${isExpired ? 'border-red-200 bg-red-50/30' : isUrgent ? 'border-orange-200 bg-orange-50/30' : 'border-primary/20'}`} data-testid="renewal-card">
      <CardContent className="p-5 space-y-4">
        {/* Warning Banner */}
        {(isUrgent || isExpired) && (
          <div className={`flex items-start gap-3 rounded-lg p-3 ${isExpired ? 'bg-red-100' : 'bg-orange-100'}`}>
            <AlertTriangle className={`h-5 w-5 shrink-0 ${isExpired ? 'text-red-600' : 'text-orange-600'}`} />
            <div>
              <p className={`font-semibold text-sm ${isExpired ? 'text-red-800' : 'text-orange-800'}`}>
                {isExpired ? 'Aboneliğiniz sona erdi!' : `Aboneliğinizin bitmesine ${daysLeft} gün kaldı!`}
              </p>
              <p className={`text-xs mt-0.5 ${isExpired ? 'text-red-600' : 'text-orange-600'}`}>
                {isExpired ? 'Hizmetlerinizi kesintisiz kullanmak için aboneliğinizi yenileyin.' : 'Süresiz erişim için aboneliğinizi şimdi yenileyin.'}
              </p>
            </div>
          </div>
        )}

        {/* CTA or Renewal Form */}
        {!showRenewal ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-primary" />
              <div>
                <p className="font-semibold text-sm">Abonelik Yenile</p>
                <p className="text-xs text-muted-foreground">Paket seçin ve ödeme yapın</p>
              </div>
            </div>
            <Button onClick={() => setShowRenewal(true)} data-testid="open-renewal-btn">
              <RefreshCw className="mr-2 h-4 w-4" />Yenile
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <RefreshCw className="h-4 w-4 text-primary" />
                Abonelik Yenileme
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowRenewal(false)}>Kapat</Button>
            </div>

            {/* Package Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Paket Seçin</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="renewal-package-selection">
                {packages.filter(p => p.slug !== 'trial').map((pkg) => {
                  const isSelected = renewPkg === pkg.slug;
                  const yp = pkg.yearly_price || (pkg.monthly_price * 12);
                  return (
                    <button
                      key={pkg.id}
                      type="button"
                      onClick={() => setRenewPkg(pkg.slug)}
                      className={`rounded-xl border-2 p-3 text-left transition-all ${isSelected ? 'border-primary bg-primary/5' : 'border-transparent bg-muted/30 hover:border-primary/30'}`}
                      data-testid={`renew-pkg-${pkg.slug}`}
                    >
                      {isSelected && <CheckCircle className="h-4 w-4 text-primary mb-1" />}
                      <p className="font-semibold text-sm">{pkg.name}</p>
                      <p className={`font-bold text-base ${isSelected ? 'text-primary' : ''}`}>{yp.toLocaleString('tr-TR')} TL<span className="text-xs font-normal">/yıl</span></p>
                      <div className="mt-1 space-y-0.5">
                        <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                          <Users className="h-3 w-3" />{pkg.is_unlimited_personnel ? 'Sınırsız' : pkg.personnel_limit} Çalışan
                        </p>
                        <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                          <MessageCircle className="h-3 w-3" />{pkg.is_unlimited_messages ? 'Sınırsız' : pkg.monthly_message_limit} Mesaj
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Order Summary */}
            {selectedPkg && (
              <div className="rounded-lg border-2 border-primary/20 bg-primary/5 p-3 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Paket:</span>
                  <span className="font-semibold">{selectedPkg.name}</span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="font-medium">Yıllık Tutar:</span>
                  <span className="text-lg font-bold text-primary">{yearlyPrice.toLocaleString('tr-TR')} TL</span>
                </div>
              </div>
            )}

            {/* Payment Methods */}
            {renewPkg && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Ödeme Yöntemi</Label>
                {paymentMethods.length === 0 ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    <p className="flex items-center gap-1"><Info className="h-4 w-4" />Aktif ödeme yöntemi bulunmuyor. Lütfen yöneticinize başvurun.</p>
                  </div>
                ) : (
                  <div className="grid gap-2" data-testid="renewal-payment-methods">
                    {paymentMethods.map(pm => (
                      <button
                        key={pm.id}
                        type="button"
                        onClick={() => setRenewPm(pm)}
                        className={`rounded-lg border-2 p-3 text-left transition-all flex items-center gap-3 ${renewPm?.id === pm.id ? 'border-primary bg-primary/5' : 'border-transparent bg-muted/20 hover:border-primary/30'}`}
                        data-testid={`renew-pm-${pm.type}`}
                      >
                        <div className={`rounded-lg p-2 ${pm.type === 'credit_card' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'}`}>
                          {pm.type === 'credit_card' ? <CreditCard className="h-4 w-4" /> : <Building className="h-4 w-4" />}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm">{pm.name}</p>
                          {pm.description && <p className="text-[11px] text-muted-foreground">{pm.description}</p>}
                        </div>
                        {renewPm?.id === pm.id && <CheckCircle className="h-4 w-4 text-primary shrink-0" />}
                      </button>
                    ))}
                  </div>
                )}

                {/* Bank transfer details */}
                {renewPm?.type === 'bank_transfer' && renewPm?.iban && (
                  <div className="rounded-lg border bg-green-50 p-3 space-y-1 text-sm" data-testid="renewal-bank-details">
                    <p className="text-[11px] font-semibold text-green-800 uppercase tracking-wider">Havale Bilgileri</p>
                    {renewPm.bank_name && <p className="text-green-700">Banka: <span className="font-medium">{renewPm.bank_name}</span></p>}
                    <div className="flex items-center gap-2">
                      <p className="text-green-700">IBAN: <span className="font-mono font-medium">{renewPm.iban}</span></p>
                      <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyIban(renewPm.iban)}><Copy className="h-3 w-3" /></Button>
                    </div>
                    {renewPm.account_holder && <p className="text-green-700">Hesap Sahibi: <span className="font-medium">{renewPm.account_holder}</span></p>}
                    <p className="text-[10px] text-green-600 mt-1">Havale yaptıktan sonra yönetici onayı ile aboneliğiniz aktif edilecektir.</p>
                  </div>
                )}

                {renewPm?.type === 'credit_card' && (
                  <div className="rounded-lg border bg-blue-50 p-3 text-sm text-blue-800" data-testid="renewal-cc-details">
                    <p className="flex items-center gap-1"><CreditCard className="h-4 w-4" />Kredi kartı ile ödeme sonrası aboneliğiniz anında aktif edilecektir.</p>
                  </div>
                )}
              </div>
            )}

            {/* Submit */}
            <Button className="w-full h-11 font-semibold" onClick={handleRenew} disabled={isRenewing || !renewPkg} data-testid="submit-renewal-btn">
              {isRenewing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              {renewPm?.type === 'bank_transfer' ? 'Yenileme Talebi Gönder' : 'Aboneliği Yenile'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function SettingsPage() {
  const { t } = useI18n();
  const { user } = useAuthStore();
  const { company, isLoading, fetchCompany, updateCompany, uploadLogo, renewSubscription } = useCompanyStore();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    tax_number: '',
    tax_office: '',
    cron_hour: 9,
    cron_minute: 0,
    primary_color: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Renewal state
  const [showRenewal, setShowRenewal] = useState(false);
  const [packages, setPackages] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [renewPkg, setRenewPkg] = useState('');
  const [renewPm, setRenewPm] = useState(null);
  const [isRenewing, setIsRenewing] = useState(false);
  const [subHistory, setSubHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [renewPaytrToken, setRenewPaytrToken] = useState(null);
  const [renewPaytrOid, setRenewPaytrOid] = useState(null);
  const [checkingRenewPayment, setCheckingRenewPayment] = useState(false);

  const pollRenewPayment = async () => {
    if (!renewPaytrOid) return;
    setCheckingRenewPayment(true);
    try {
      const res = await axios.get(`${API_URL}/api/paytr/status/${renewPaytrOid}`);
      if (res.data?.status === 'success') {
        toast.success('Ödemeniz başarıyla alınmıştır. Aboneliğiniz yenilendi.', { duration: 8000 });
        setRenewPaytrToken(null);
        setRenewPaytrOid(null);
        setShowRenewal(false);
        fetchCompany();
      } else if (res.data?.status === 'failed') {
        toast.error('Ödeme başarısız oldu.');
        setRenewPaytrToken(null);
        setRenewPaytrOid(null);
      } else {
        toast.info('Ödeme henüz tamamlanmadı. Lütfen ödemeyi tamamlayıp tekrar kontrol edin.');
      }
    } catch {}
    setCheckingRenewPayment(false);
  };

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      fetchCompany();
      // Fetch subscription history
      setHistoryLoading(true);
      const token = require('@/store').useAuthStore.getState().token;
      axios.get(`${API_URL}/api/subscription-history`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setSubHistory(res.data))
        .catch(() => {})
        .finally(() => setHistoryLoading(false));
    }
    // Fetch packages and payment methods for renewal
    axios.get(`${API_URL}/api/packages`).then(res => setPackages(res.data)).catch(() => {});
    axios.get(`${API_URL}/api/payment-methods/active`).then(res => setPaymentMethods(res.data)).catch(() => {});
  }, [fetchCompany, user?.role]);

  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name || '',
        email: company.email || '',
        phone: company.phone || '',
        address: company.address || '',
        tax_number: company.tax_number || '',
        tax_office: company.tax_office || '',
        cron_hour: company.cron_hour || 9,
        cron_minute: company.cron_minute || 0,
        primary_color: company.primary_color || '',
      });
    }
  }, [company]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const cleanData = {
      name: formData.name || null,
      email: formData.email?.trim() || null,
      phone: formData.phone?.trim() || null,
      address: formData.address?.trim() || null,
      tax_number: formData.tax_number?.trim() || null,
      tax_office: formData.tax_office?.trim() || null,
      cron_hour: formData.cron_hour,
      cron_minute: formData.cron_minute,
    };
    const result = await updateCompany(company.id, cleanData);
    setIsSubmitting(false);
    if (result.success) {
      toast.success(t('settingsSaved'));
    } else {
      toast.error(result.error);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Sadece resim dosyası yüklenebilir');
    if (file.size > 2 * 1024 * 1024) return toast.error('Dosya boyutu 2MB\'dan büyük olamaz');
    setIsUploading(true);
    const result = await uploadLogo(company.id, file);
    setIsUploading(false);
    if (result.success) {
      toast.success('Logo yüklendi');
    } else {
      toast.error(result.error);
    }
  };

  if (user?.role === 'super_admin') {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="settings-page">
        <div>
          <h1 className="font-heading text-2xl font-bold sm:text-3xl">Ayarlar</h1>
          <p className="mt-1 text-muted-foreground">Super Admin için firma ayarları mevcut değil</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const logoUrl = company?.logo ? `${process.env.REACT_APP_BACKEND_URL}${company.logo}` : null;
  const subInfo = getSubscriptionInfo(company);
  const SubIcon = subInfo.icon;

  return (
    <div className="space-y-8 animate-fade-in" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl font-bold sm:text-3xl">Ayarlar</h1>
        <p className="mt-1 text-muted-foreground">Firma ayarlarınızı yönetin</p>
      </div>

      {/* Subscription Info */}
      <Card data-testid="subscription-info-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-primary" />
            Abonelik Bilgisi
          </CardTitle>
          <CardDescription>Mevcut abonelik durumunuz ve paket bilgileriniz</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant="outline" className={`gap-1.5 px-3 py-1 text-sm ${subInfo.color}`} data-testid="subscription-status-badge">
                <SubIcon className="h-3.5 w-3.5" />
                {subInfo.label}
              </Badge>
              {company?.package_name && (
                <Badge variant="secondary" className="gap-1.5 px-3 py-1 text-sm" data-testid="package-name-badge">
                  {company.package_name}
                </Badge>
              )}
            </div>
            {/* Package Details */}
            {company?.package_name && company?.subscription_status === 'active' && (
              <div className="rounded-lg bg-primary/5 border border-primary/20 p-4" data-testid="active-package-info">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-medium text-sm">{company.package_name} Paket</p>
                  <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">Yıllık Abonelik</Badge>
                </div>
                <div className="grid gap-2 grid-cols-2 sm:grid-cols-4 text-xs">
                  <div className="rounded bg-background p-2">
                    <p className="text-muted-foreground">Yıllık Ücret</p>
                    <p className="font-bold text-sm mt-0.5">{(company.subscription_price || 0).toLocaleString('tr-TR')} TL</p>
                    <p className="text-[10px] text-muted-foreground">Aylık {Math.round((company.subscription_price || 0) / 12).toLocaleString('tr-TR')} TL</p>
                  </div>
                  <div className="rounded bg-background p-2">
                    <p className="text-muted-foreground">Personel Limiti</p>
                    <p className="font-bold text-sm mt-0.5">{company.limits?.personnel_limit >= 9999 ? 'Sınırsız' : company.limits?.personnel_limit}</p>
                  </div>
                  <div className="rounded bg-background p-2">
                    <p className="text-muted-foreground">Aylık Mesaj</p>
                    <p className="font-bold text-sm mt-0.5">{company.limits?.monthly_message_limit >= 9999 ? 'Sınırsız' : company.limits?.monthly_message_limit}</p>
                  </div>
                  <div className="rounded bg-background p-2">
                    <p className="text-muted-foreground">Cari Limiti</p>
                    <p className="font-bold text-sm mt-0.5">{company.limits?.cari_limit >= 9999 ? 'Sınırsız' : company.limits?.cari_limit}</p>
                  </div>
                </div>
              </div>
            )}
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
              {company?.subscription_status === 'trial' ? (
                <>
                  <div className="rounded-lg border p-3" data-testid="trial-start-date">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                      <CalendarDays className="h-3.5 w-3.5" />
                      Deneme Başlangıcı
                    </div>
                    <p className="font-medium text-sm">{formatDateTR(company?.trial_start)}</p>
                  </div>
                  <div className="rounded-lg border p-3" data-testid="trial-end-date">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                      <CalendarDays className="h-3.5 w-3.5" />
                      Deneme Bitiş
                    </div>
                    <p className="font-medium text-sm">{formatDateTR(company?.trial_end)}</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="rounded-lg border p-3" data-testid="subscription-start-date">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                      <CalendarDays className="h-3.5 w-3.5" />
                      Abonelik Başlangıcı
                    </div>
                    <p className="font-medium text-sm">{formatDateTR(company?.subscription_start)}</p>
                  </div>
                  <div className="rounded-lg border p-3" data-testid="subscription-end-date">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                      <CalendarDays className="h-3.5 w-3.5" />
                      Abonelik Bitiş
                    </div>
                    <p className="font-medium text-sm">{formatDateTR(company?.subscription_end)}</p>
                  </div>
                </>
              )}
            </div>
            {company?.subscription_status === 'trial' && (
              <p className="text-xs text-muted-foreground">
                Deneme süreniz dolduğunda abonelik satın almanız gerekecektir. Destek için yöneticinize başvurun.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Subscription Renewal Section */}
      <RenewalSection
        company={company}
        packages={packages}
        paymentMethods={paymentMethods}
        showRenewal={showRenewal}
        setShowRenewal={setShowRenewal}
        renewPkg={renewPkg}
        setRenewPkg={setRenewPkg}
        renewPm={renewPm}
        setRenewPm={setRenewPm}
        isRenewing={isRenewing}
        setIsRenewing={setIsRenewing}
        renewSubscription={renewSubscription}
        fetchCompany={fetchCompany}
        user={user}
        setRenewPaytrToken={setRenewPaytrToken}
        setRenewPaytrOid={setRenewPaytrOid}
      />

      {/* Subscription History */}
      <Card data-testid="subscription-history-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-primary" />
            Abonelik Geçmişi
          </CardTitle>
          <CardDescription>Geçmiş ödeme ve abonelik işlemleriniz</CardDescription>
        </CardHeader>
        <CardContent>
          {historyLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : subHistory.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">Henüz abonelik geçmişi bulunmuyor.</p>
          ) : (
            <div className="space-y-2" data-testid="history-list">
              {subHistory.map((item) => {
                const evLabels = { registration: 'İlk Kayıt', renewal: 'Yenileme', payment_approved: 'Ödeme Onayı', package_assigned: 'Paket Atama' };
                const stConfig = {
                  active: { label: 'Aktif', cls: 'bg-green-100 text-green-700' },
                  pending: { label: 'Beklemede', cls: 'bg-amber-100 text-amber-700' },
                  expired: { label: 'Süresi Dolmuş', cls: 'bg-red-100 text-red-700' },
                };
                const pmLabels = { credit_card: 'Kredi Kartı', bank_transfer: 'Havale/EFT', admin: 'Admin' };
                const st = stConfig[item.status] || stConfig.pending;
                const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '-'; } };
                return (
                  <div key={item.id} className="flex items-center justify-between rounded-lg border p-3 gap-3" data-testid={`history-item-${item.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`rounded-lg p-2 shrink-0 ${item.payment_method === 'credit_card' ? 'bg-blue-50 text-blue-600' : item.payment_method === 'bank_transfer' ? 'bg-green-50 text-green-600' : 'bg-gray-50 text-gray-600'}`}>
                        {item.payment_method === 'credit_card' ? <CreditCard className="h-4 w-4" /> : item.payment_method === 'bank_transfer' ? <Building className="h-4 w-4" /> : <Package className="h-4 w-4" />}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">{evLabels[item.event_type] || item.event_type}</span>
                          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${st.cls}`}>{st.label}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                          {item.package_name} · {pmLabels[item.payment_method] || item.payment_method} · {fmtDate(item.created_at)}
                        </p>
                      </div>
                    </div>
                    <span className="font-bold text-sm text-primary whitespace-nowrap">{(item.amount || 0).toLocaleString('tr-TR')} TL</span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* KVKK / Legal Consents */}
      <Card data-testid="legal-consents-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Sözleşme Onayları
          </CardTitle>
          <CardDescription>KVKK, gizlilik ve üyelik sözleşme onay durumlarınız</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-lg border p-3" data-testid="kvkk-consent-status">
              <div className="flex items-center gap-3">
                <Shield className="h-4 w-4 text-primary shrink-0" />
                <div>
                  <p className="text-sm font-medium">KVKK Aydınlatma Metni</p>
                  <a href="/legal/kvkk" target="_blank" className="text-xs text-primary hover:underline">Metni görüntüle</a>
                </div>
              </div>
              {user?.kvkk_accepted_at ? (
                <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300 gap-1 text-xs">
                  <CheckCircle2 className="h-3 w-3" />
                  {formatDateTR(user.kvkk_accepted_at)}
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-gray-100 text-gray-500 border-gray-300 text-xs">Onaylanmadı</Badge>
              )}
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3" data-testid="privacy-consent-status">
              <div className="flex items-center gap-3">
                <Lock className="h-4 w-4 text-primary shrink-0" />
                <div>
                  <p className="text-sm font-medium">Gizlilik Politikası</p>
                  <a href="/legal/gizlilik" target="_blank" className="text-xs text-primary hover:underline">Politikayı görüntüle</a>
                </div>
              </div>
              {user?.privacy_accepted_at ? (
                <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300 gap-1 text-xs">
                  <CheckCircle2 className="h-3 w-3" />
                  {formatDateTR(user.privacy_accepted_at)}
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-gray-100 text-gray-500 border-gray-300 text-xs">Onaylanmadı</Badge>
              )}
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3" data-testid="membership-consent-status">
              <div className="flex items-center gap-3">
                <FileText className="h-4 w-4 text-primary shrink-0" />
                <div>
                  <p className="text-sm font-medium">Üyelik Sözleşmesi</p>
                  <a href="/legal/uyelik" target="_blank" className="text-xs text-primary hover:underline">Sözleşmeyi görüntüle</a>
                </div>
              </div>
              {user?.membership_accepted_at ? (
                <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300 gap-1 text-xs">
                  <CheckCircle2 className="h-3 w-3" />
                  {formatDateTR(user.membership_accepted_at)}
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-gray-100 text-gray-500 border-gray-300 text-xs">Onaylanmadı</Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logo Upload */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-primary" />
            Firma Logosu
          </CardTitle>
          <CardDescription>Firma logonuzu yükleyin (max 2MB, PNG/JPG)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-border bg-muted/30">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" className="h-full w-full object-contain" data-testid="company-logo-preview" />
              ) : (
                <Building2 className="h-8 w-8 text-muted-foreground" />
              )}
            </div>
            <div>
              <input type="file" ref={fileInputRef} accept="image/*" className="hidden" onChange={handleLogoUpload} data-testid="logo-file-input" />
              <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={isUploading} data-testid="upload-logo-btn">
                {isUploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                Logo Yükle
              </Button>
              {logoUrl && <p className="mt-1 text-xs text-muted-foreground">Mevcut logo yüklenmiş</p>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Theme Color removed - color is managed by system admin */}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Company Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              Firma Bilgileri
            </CardTitle>
            <CardDescription>Firma temel bilgilerini düzenleyin</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">{t('companyName')}</Label>
                <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Firma adı" data-testid="settings-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">{t('email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="info@firma.com" className="pl-10" data-testid="settings-email-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">{t('phone')}</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input id="phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="0XXX XXX XX XX" className="pl-10" data-testid="settings-phone-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">{t('address')}</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input id="address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="Firma adresi" className="pl-10" data-testid="settings-address-input" />
                </div>
              </div>
            </div>
            <Separator />
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="tax_number">Vergi Numarası</Label>
                <Input id="tax_number" value={formData.tax_number} onChange={(e) => setFormData({ ...formData, tax_number: e.target.value })} placeholder="Vergi numarası" data-testid="settings-tax-number-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tax_office">Vergi Dairesi</Label>
                <Input id="tax_office" value={formData.tax_office} onChange={(e) => setFormData({ ...formData, tax_office: e.target.value })} placeholder="Vergi dairesi" data-testid="settings-tax-office-input" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cron Settings */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              Otomatik Mesaj Saati
            </CardTitle>
            <CardDescription>Vade günü hatırlatma mesajlarının gönderileceği saati belirleyin</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="cron_hour">Saat</Label>
                <Input id="cron_hour" type="number" min="0" max="23" value={formData.cron_hour} onChange={(e) => setFormData({ ...formData, cron_hour: parseInt(e.target.value) || 0 })} className="w-20" data-testid="settings-cron-hour-input" />
              </div>
              <span className="mt-6 text-xl font-medium">:</span>
              <div className="space-y-1.5">
                <Label htmlFor="cron_minute">Dakika</Label>
                <Input id="cron_minute" type="number" min="0" max="59" value={formData.cron_minute} onChange={(e) => setFormData({ ...formData, cron_minute: parseInt(e.target.value) || 0 })} className="w-20" data-testid="settings-cron-minute-input" />
              </div>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              Her gün saat {formData.cron_hour.toString().padStart(2, '0')}:{formData.cron_minute.toString().padStart(2, '0')}'de vadesi gelen ödemeler için hatırlatma mesajı gönderilecektir.
            </p>
          </CardContent>
        </Card>

        {/* Limits Info */}
        {company?.limits && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>Paket Limitleri</CardTitle>
              <CardDescription>Mevcut paketinizin limitleri</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-5">
                {[
                  { label: 'Günlük Mesaj', value: company.limits.daily_message_limit },
                  { label: 'Aylık Mesaj', value: company.limits.monthly_message_limit },
                  { label: t('rolePersonnel'), value: company.limits.personnel_limit },
                  { label: 'Cari', value: company.limits.cari_limit },
                  { label: 'Ödeme Sözü', value: company.limits.payment_limit },
                ].map((item) => (
                  <div key={item.label} className="rounded-lg bg-muted/50 p-3 sm:p-4">
                    <p className="text-xs sm:text-sm text-muted-foreground">{item.label}</p>
                    <p className="font-heading text-lg sm:text-xl font-bold mt-1">{item.value}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <Button type="submit" disabled={isSubmitting} data-testid="save-settings-btn">
            {isSubmitting ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Kaydediliyor...</>
            ) : (
              <><Save className="mr-2 h-4 w-4" />{t('save')}</>
            )}
          </Button>
        </div>
      </form>

      {/* PayTR iFrame Overlay for Renewal */}
      {renewPaytrToken && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="paytr-renew-overlay">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b bg-primary/5">
              <div className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-primary" />
                <h3 className="font-semibold text-sm">Abonelik Yenileme Ödemesi</h3>
              </div>
            </div>
            <div className="p-1" style={{ minHeight: '400px' }}>
              <iframe
                src={`https://www.paytr.com/odeme/guvenli/${renewPaytrToken}`}
                id="paytriframe-renew"
                frameBorder="0"
                scrolling="yes"
                style={{ width: '100%', minHeight: '400px' }}
                data-testid="paytr-renew-iframe"
              />
            </div>
            <div className="flex items-center justify-between px-5 py-3 border-t bg-muted/30">
              <Button
                variant="ghost" size="sm"
                onClick={() => { setRenewPaytrToken(null); setRenewPaytrOid(null); toast.info('Ödeme iptal edildi.'); }}
                data-testid="paytr-renew-cancel-btn"
              >
                İptal Et
              </Button>
              <Button
                size="sm"
                onClick={pollRenewPayment}
                disabled={checkingRenewPayment}
                data-testid="paytr-renew-check-btn"
              >
                {checkingRenewPayment ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="mr-2 h-3.5 w-3.5" />}
                Ödeme Durumunu Kontrol Et
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
